/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCatalogId, IActionContext } from '@msdyn365-commerce/core';
import {
    Address,
    Cart,
    CartLine,
    ChannelDeliveryOptionConfiguration,
    OrgUnitLocation,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    AttributeValueBase,
    CommerceProperty,
    LineDeliverySpecification,
    ReleasedProductType
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { action, computed, observable } from 'mobx';

import { GlobalState } from '../global-state/global-state';
import { ICartActionResult, ICartState } from '../state-interfaces/i-cart-state';
import addInvoicesToCartInternal from './add-invoices-to-cart';
import addOrderInvoiceToCartInternal from './add-order-invoice-to-cart';
import addProductToCartInternal, { addProductsToCartInternal } from './add-product-to-cart';
import addPromoCodeInternal from './add-promo-code';
import { ICartActionResultWithCart } from './cart-action-result';
import clearCartLineDeliveryModeInternal from './clear-cart-line-delivery-mode';
import clearCartLinesDeliveryInformation from './clear-cart-lines-delivery-information';
import getOrCreateActiveCart from './get-or-create-active-cart';
import refreshCartInternal from './refresh-cart';
import removeAllPromoCodesInternal from './remove-all-promo-codes';
import removeCartLineInternal from './remove-cart-lines';
import removePromoCodesInternal from './remove-promo-codes';
import setInvoiceLinePriceInternal from './set-invoice-line-price';
import updateCart from './update-cart';
import updateCartLineDeliverySpecificationsInternal from './update-cart-line-delivery-specifications';
import updateCartLineQuantityInternal from './update-cart-line-quantity';
import updateLoyaltyCardIdInternal from './update-loyalty-card-id';
import updateReceiptEmail from './update-receipt-email';
import updateShippingAddress from './update-shipping-address';
import updateCartLinesInternal from './update-cart-lines';

/**
 * Cart state information.
 */
export class BaseCartState extends GlobalState implements ICartState {
    @observable protected _cart: Cart;

    @computed public get cart(): Readonly<Cart> {
        return this._cart;
    }

    @observable protected _attributeValues: AttributeValueBase[];

    @computed public get attributeValues(): AttributeValueBase[] {
        return this._attributeValues;
    }

    @observable protected _extensionProperties: CommerceProperty[];

    @computed public get extensionProperties(): CommerceProperty[] {
        return this._extensionProperties;
    }

    @computed public get totalItemsInCart(): number {
        if (this._cart.CartLines) {
            return this._cart.CartLines.map(cartLine => cartLine.Quantity || 1).reduce((total, num) => total + num, 0);
        }

        return 0;
    }

    @observable protected _isProductAddedToCart: boolean;

    @computed public get isProductAddedToCart(): boolean {
        return this._isProductAddedToCart;
    }

    @computed public get isEmpty(): boolean {
        return !(this._cart.CartLines && this._cart.CartLines.length > 0);
    }

    @computed public get hasInvoiceLine(): boolean {
        return !!(this._cart.CartLines && this._cart.CartLines.some(cartline => cartline.IsInvoiceLine === true));
    }

    constructor(actionContext: IActionContext) {
        super(actionContext);
        this._cart = <Cart>{};
        this._attributeValues = [];
        this._extensionProperties = [];
        this._isProductAddedToCart = false;
    }

    /**
     * Refreshes the cart by getting it from the server.
     *
     * Other actions should keep cart up to date so shouldn't need to call this
     * outside of initialization but still might be scenarios where a manual
     * refresh is needed.
     * @param input - Input.
     * @param input.additionalProperties - Additional properties.
     * @returns - The cart action result.
     */
    @action
    public async refreshCart(input: { additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            const newCart = await refreshCartInternal(this.cart.Id, this.actionContext);

            if (newCart) {
                this._cart = newCart;

                return { status: 'SUCCESS' };
            }

            return { status: 'FAILED' };
        });
    }

    /**
     * Adds the specified product to the current cart. If product is already in cart
     * will update its cart line, otherwise will add a new cart line to the cart.
     * @param input - The input.
     * @param input.product - The product to add to the cart.
     * @param input.count - How many copies of the product to add.
     * @param input.location - The org unit location, used for BuyOnlinePickupInStore scenarios
     * (If you want item to simply be shipped, leave this parameter undefined).
     * @param input.additionalProperties - Additional properties.
     * @param input.availableQuantity - Available quantity.
     * @param input.enableStockCheck - Is stock check enabled.
     * @param input.isAddEmailDeliveryItemToCart - Is add email delivery item to cart.
     * @param input.isPriceKeyedIn - Is price keyed in.
     * @param input.customPrice - Custom price.
     * @param input.deliveryMode - Delivery mode.
     * @param input.shouldSkipSiteSettings - Should skip site settings.
     * @param input.catalogId - Should skip site settings.
     * @returns Cart action result.
     */
    @action
    public async addProductToCart(input: {
        product: SimpleProduct;
        count?: number;
        location?: OrgUnitLocation;
        additionalProperties?: object;
        availableQuantity?: number;
        enableStockCheck?: boolean;
        isAddEmailDeliveryItemToCart?: boolean;
        isPriceKeyedIn?: boolean;
        customPrice?: number;
        deliveryMode?: string;
        shouldSkipSiteSettings?: boolean;
        catalogId?: number;
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            const cartLine: CartLine = {
                CatalogId: input.catalogId ?? getCatalogId(this.actionContext.requestContext),
                Description: input.product.Description,

                // TODO: Investigate this value and what it represents
                EntryMethodTypeValue: 3,
                ItemId: input.product.ItemId,
                ProductId: input.product.RecordId,
                Quantity: input.count || 1,
                TrackingId: '',
                UnitOfMeasureSymbol: input.product.DefaultUnitOfMeasure,
                IsPriceKeyedIn: input.isPriceKeyedIn,
                IsGiftCardLine: input.product.IsGiftCard,
                Price: input.customPrice ? input.customPrice : input.product.Price
            };

            if (input.location) {
                if (!this.actionContext.requestContext.channel) {
                    return { status: 'FAILED' };
                }

                // If curbside pick is not available use the default one
                if (input.deliveryMode !== undefined) {
                    cartLine.DeliveryMode = input.deliveryMode;
                } else {
                    cartLine.DeliveryMode = this.actionContext.requestContext.channel.PickupDeliveryModeCode;
                }

                cartLine.FulfillmentStoreId = input.location.OrgUnitNumber;
                cartLine.WarehouseId = input.location.InventoryLocationId;
                cartLine.ShippingAddress = this._buildAddressFromOrgUnitLocation(input.location);
            }

            if (input.isAddEmailDeliveryItemToCart) {
                if (!this.actionContext.requestContext.channel) {
                    return { status: 'FAILED' };
                }

                cartLine.DeliveryMode = this.actionContext.requestContext.channel.EmailDeliveryModeCode;
            }

            // Check if the product is service or not by product type
            const isAddServiceItemToCart = input.product.ItemTypeValue === ReleasedProductType.Service;

            // @ts-expect-error
            const orderQuantityLimitsFeatureIsEnabled: boolean = !!input.additionalProperties?.orderQuantityLimitsFeatureIsEnabled;
            const isMaxByQuantityValidationFeatureDefined: boolean = !!input.product.Behavior?.MaximumQuantity;
            return this._doCartOperationWithRetry(() =>
                addProductToCartInternal(
                    this.cart,
                    cartLine,
                    this.actionContext,
                    orderQuantityLimitsFeatureIsEnabled,
                    input.availableQuantity,
                    input.enableStockCheck,
                    isAddServiceItemToCart,
                    isMaxByQuantityValidationFeatureDefined,
                    input.shouldSkipSiteSettings
                )
            );
        });
    }

    /**
     * Adds the specified product to the current cart. If product is already in cart
     * will update its cart line, otherwise will add a new cart line to the cart.
     * @param input - Input.
     * @param input.product - Product.
     * @param input.count - Count.
     * @param input.location - Location.
     * @param input.additionalProperties - Additional properties.
     * @param input.availableQuantity - Available quantity.
     * @param input.enableStockCheck - Enable stock check.
     * @param input.isAddEmailDeliveryItemToCart - Is add email delivery item to cart.
     * @param input.isPriceKeyedIn - Is price keyed in.
     * @param input.customPrice - Custom price.
     * @param input.deliveryMode - Delivery mode.
     * @returns Cart action result.
     */
    @action
    public async addProductsToCart(
        input: {
            product: SimpleProduct;
            count?: number;
            location?: OrgUnitLocation;
            additionalProperties?: object;
            availableQuantity?: number;
            enableStockCheck?: boolean;
            isAddEmailDeliveryItemToCart?: boolean;
            isPriceKeyedIn?: boolean;
            customPrice?: number;
            deliveryMode?: string;
            catalogId?: number;
        }[]
    ): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            const internalInput: {
                cartLineToAdd: CartLine;
                availableQuantity?: number;
                isStockCheckEnabled?: boolean;
                isAddServiceItemToCart?: boolean;
            }[] = [];

            for (const inputItem of input) {
                const cartLine: CartLine = {
                    CatalogId: inputItem.catalogId ?? getCatalogId(this.actionContext.requestContext),
                    Description: inputItem.product.Description,

                    // TODO: Investigate this value and what it represents
                    EntryMethodTypeValue: 3,
                    ItemId: inputItem.product.ItemId,
                    ProductId: inputItem.product.RecordId,
                    Quantity: inputItem.count || 1,
                    TrackingId: '',
                    UnitOfMeasureSymbol: inputItem.product.DefaultUnitOfMeasure,
                    IsPriceKeyedIn: inputItem.isPriceKeyedIn,
                    IsGiftCardLine: inputItem.product.IsGiftCard,
                    Price: inputItem.customPrice ? inputItem.customPrice : inputItem.product.Price
                };

                if (inputItem.location) {
                    if (!this.actionContext.requestContext.channel) {
                        return { status: 'FAILED' };
                    }

                    // If curbside pick is not available use the default one
                    if (inputItem.deliveryMode !== undefined) {
                        cartLine.DeliveryMode = inputItem.deliveryMode;
                    } else {
                        cartLine.DeliveryMode = this.actionContext.requestContext.channel.PickupDeliveryModeCode;
                    }

                    cartLine.FulfillmentStoreId = inputItem.location.OrgUnitNumber;
                    cartLine.WarehouseId = inputItem.location.InventoryLocationId;
                    cartLine.ShippingAddress = this._buildAddressFromOrgUnitLocation(inputItem.location);
                }

                if (inputItem.isAddEmailDeliveryItemToCart) {
                    cartLine.DeliveryMode = this.actionContext.requestContext.channel?.EmailDeliveryModeCode;
                }

                // Check if the product is service or not by product type
                const PRODUCTASSERVICE = 2 as ReleasedProductType.Service;
                const isAddServiceItemToCart = inputItem.product.ItemTypeValue === PRODUCTASSERVICE;

                internalInput.push({
                    cartLineToAdd: cartLine,
                    availableQuantity: inputItem.availableQuantity,
                    isStockCheckEnabled: inputItem.enableStockCheck,
                    isAddServiceItemToCart
                });
            }

            return this._doCartOperationWithRetry(() => addProductsToCartInternal(this.cart, this.actionContext, internalInput));
        });
    }

    /**
     * Adds the specified invoices to the current cart.
     * @param input - The input.
     * @param input.invoiceIds - Invoice Ids.
     * @param input.additionalProperties - Additional properties.
     * @returns Cart action result.
     */
    @action
    public async addInvoicesToCart(input: { invoiceIds: string[]; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => addInvoicesToCartInternal(this.cart, input.invoiceIds, this.actionContext));
        });
    }

    /**
     * Adds the specified order invoice to the current cart.
     * @param input - Input.
     * @param input.invoiceId - Invoice id.
     * @param input.lineDescription - Line description.
     * @param input.additionalProperties - Additional properties.
     * @returns Cart action result.
     */
    @action
    public async addOrderInvoiceToCart(input: {
        invoiceId: string;
        lineDescription: string;
        additionalProperties?: object;
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() =>
                addOrderInvoiceToCartInternal(this.cart, input.invoiceId, input.lineDescription, this.actionContext)
            );
        });
    }

    /**
     * Sets price for the given cart line with an invoice. Used to complete partial invoice payments.
     * @param input - Input for the operation.
     * @param input.cartLineId - The cart line with the invoice for which to update the price.
     * @param input.price - The new value of price of the invoice.
     * @returns The cart action result.
     */
    @action public async setInvoiceLinePrice(input: { cartLineId: string | null; price: number }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(async () =>
                setInvoiceLinePriceInternal(this.cart, input.cartLineId, input.price, this.actionContext)
            );
        });
    }

    /**
     * Removes the cart lines with the provided ids from the cart.
     * @param input - Input.
     * @param input.cartLineIds - Cart line ids.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public async removeCartLines(input: { cartLineIds: string[]; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => removeCartLineInternal(this.cart, input.cartLineIds, this.actionContext));
        });
    }

    /**
     * Clears the pickup location information from the provided cart line,
     * resulting in it getting shipped to the customer.
     * @param input - Input.
     * @param input.cartLineId - Cart line id.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public async clearCartLinePickupLocation(input: { cartLineId: string; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => clearCartLineDeliveryModeInternal(this.cart, input.cartLineId, this.actionContext));
        });
    }

    /**
     * Clears the delivery mode, and other information for the shipping cart lines.
     * @param input - Input.
     * @param input.additionalProperties - Additional properties.
     * @param input.channelDeliveryOptionConfiguration -- Channel delivery option configuration.
     * @returns The cart action result.
     */
    @action
    public async clearCartLinesDeliveryInformation(input: {
        additionalProperties?: object;
        channelDeliveryOptionConfiguration?: ChannelDeliveryOptionConfiguration;
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() =>
                clearCartLinesDeliveryInformation(this.cart, this.actionContext, input.channelDeliveryOptionConfiguration)
            );
        });
    }

    /**
     * Marks the provided cart line for pickup in store, with the provided location as
     * the pickup location.
     * @param input - Input.
     * @param input.cartLineId - Cart line id.
     * @param input.location -  Location.
     * @param input.additionalProperties - Additional properties.
     * @param input.deliveryMode - Delivery mode.
     * @returns The cart action result.
     */
    @action
    public async updateCartLinePickupLocation(input: {
        cartLineId: string;
        location: OrgUnitLocation;
        additionalProperties?: object;
        deliveryMode?: string;
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            if (!this.actionContext.requestContext.channel) {
                return { status: 'FAILED' };
            }

            const cartLineDeliverySpecification = {
                LineId: input.cartLineId,
                DeliverySpecification: {
                    DeliveryModeId:
                        input.deliveryMode !== undefined
                            ? input.deliveryMode
                            : this.actionContext.requestContext.channel.PickupDeliveryModeCode,
                    DeliveryPreferenceTypeValue: 2, // Pick up in store
                    PickUpStoreId: input.location.OrgUnitNumber,
                    DeliveryAddress: this._buildAddressFromOrgUnitLocation(input.location)
                }
            };

            return this._doCartOperationWithRetry(() =>
                updateCartLineDeliverySpecificationsInternal(this.cart, [cartLineDeliverySpecification], this.actionContext)
            );
        });
    }

    /**
     * Updates cart line pick up date.
     * @param input - The cart line.
     * @param input.lineDeliverySpecifications - Line delivery specifications.
     * @returns The cart action result.
     */
    @action
    public async updateCartLinePickupDateTimeslot(input: {
        lineDeliverySpecifications: LineDeliverySpecification[];
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() =>
                updateCartLineDeliverySpecificationsInternal(this.cart, input.lineDeliverySpecifications, this.actionContext)
            );
        });
    }

    /**
     * Updates the delivery mode for items in the cart with the desired delivery mode, preserving BOPIS status for stuff already marked as BOPIS.
     * @param input - The input.
     * @param input.deliveryModeId - Delivery mode id.
     * @param input.shippingAddress - Shipping address.
     * @param input.additionalProperties - Additional properties.
     * @param input.channelDeliveryOptionConfig - Channel delivery option configuration.
     * @returns The cart action result.
     */
    @action
    public async updateCartDeliverySpecification(input: {
        deliveryModeId: string;
        shippingAddress: Address | undefined;
        additionalProperties?: object;
        channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            if (input.deliveryModeId.trim() === '') {
                return { status: 'FAILED', substatus: 'EMPTYINPUT' };
            }

            const pickupDeliveryModeCode =
                this.actionContext.requestContext.channel && this.actionContext.requestContext.channel.PickupDeliveryModeCode;
            const emailDeliveryModeCode =
                this.actionContext.requestContext.channel && this.actionContext.requestContext.channel.EmailDeliveryModeCode;
            let cartLinesForShipping: CartLine[] = [];

            cartLinesForShipping =
                input.channelDeliveryOptionConfig !== undefined
                    ? (this.cart.CartLines || []).filter(cartLine =>
                          cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                              ? cartLine.DeliveryMode !==
                                    input.channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(
                                        deliveryMode => deliveryMode === cartLine.DeliveryMode
                                    ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                              : cartLine
                      )
                    : (this.cart.CartLines || []).filter(cartLine =>
                          cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                              ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                              : cartLine
                      );

            if (cartLinesForShipping.length > 0) {
                const deliverySpecifications = cartLinesForShipping.map(cartLine => {
                    return {
                        LineId: cartLine.LineId,
                        DeliverySpecification: {
                            DeliveryModeId: input.deliveryModeId,
                            DeliveryPreferenceTypeValue: 1, // Ship
                            DeliveryAddress: input.shippingAddress
                        }
                    };
                });

                return this._doCartOperationWithRetry(() =>
                    updateCartLineDeliverySpecificationsInternal(this.cart, deliverySpecifications, this.actionContext)
                );
            }

            return { status: 'FAILED', substatus: 'NOCONTENT' };
        });
    }

    /**
     * Updates the quantity of the cart line.
     * @param input - The input.
     * @param input.cartLineId - Cart line id.
     * @param input.newQuantity - New quantity.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public async updateCartLineQuantity(input: {
        cartLineId: string;
        newQuantity: number;
        additionalProperties?: object;
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() =>
                updateCartLineQuantityInternal(
                    this.cart,
                    input.cartLineId,
                    input.newQuantity,
                    this.actionContext,

                    // @ts-expect-error
                    input.additionalProperties?.isUsingDefaultOrderSettingsMax
                )
            );
        });
    }

    /**
     * Updates the loyalty card ID on the card.
     * @param input - The input.
     * @param input.loyaltyCardNumber - The loyalty card id to use.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public async updateLoyaltyCardId(input: {
        loyaltyCardNumber: string | undefined;
        additionalProperties?: object;
    }): Promise<ICartActionResult> {
        if (!input.loyaltyCardNumber) {
            return { status: 'FAILED', substatus: 'EMPTYINPUT' };
        }

        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() =>
                updateLoyaltyCardIdInternal(this.cart, input.loyaltyCardNumber!, this.actionContext)
            );
        });
    }

    /**
     * Adds promo code to the cart.
     * @param input - The input.
     * @param input.promoCode - The promo code to add.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public addPromoCode(input: { promoCode: string; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => addPromoCodeInternal(this.cart, input.promoCode, this.actionContext));
        });
    }

    /**
     * Removes promo codes from the cart.
     * @param input - The input.
     * @param input.promoCodes - The promo codes to remove.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public removePromoCodes(input: { promoCodes: string[]; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => removePromoCodesInternal(this.cart, input.promoCodes, this.actionContext));
        });
    }

    /**
     * Removes all promo code from the cart.
     * @param input - The input.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public removeAllPromoCodes(input: { additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => removeAllPromoCodesInternal(this.cart, this.actionContext));
        });
    }

    /**
     * Updates receipt email.
     * @param input - The input.
     * @param input.newEmail - The receipt email.
     * @param input.additionalProperties - Additional properties.
     * @returns The cart action result.
     */
    @action
    public async updateReceiptEmail(input: { newEmail: string; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => updateReceiptEmail(this.cart, input.newEmail, this.actionContext));
        });
    }

    /**
     * Updates shipping address.
     * @param input - The input.
     * @param input.newShippingAddress - The shipping address.
     * @param input.additionalProperties - The additional properties.
     * @returns ICartActionResult.
     */
    @action
    public async updateShippingAddress(input: {
        newShippingAddress: Address;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(async () =>
                updateShippingAddress(this.cart, input.newShippingAddress, this.actionContext)
            );
        });
    }

    /**
     * Updates the attribute values.
     * @param input - The input.
     * @param input.newAttributeValues - The new attribute values to update.
     * @param input.additionalProperties - The additional properties.
     * @returns ICartActionResult.
     */
    @action
    public async updateAttributeValues(input: {
        newAttributeValues: AttributeValueBase[];
        additionalProperties?: object;
    }): Promise<ICartActionResult> {
        this._attributeValues = input.newAttributeValues;
        const newCartObject: Cart = {
            Id: this.cart.Id,
            AttributeValues: input.newAttributeValues
        };
        return this.updateCart({ newCartObject });
    }

    /**
     * Updates the extension properties.
     * @param input - The input.
     * @param input.newExtensionProperties - The new extension properties.
     * @param input.additionalProperties - The additional properties.
     * @returns ICartActionResult.
     */
    @action
    public async updateExtensionProperties(input: {
        newExtensionProperties: CommerceProperty[];
        additionalProperties?: object;
    }): Promise<ICartActionResult> {
        this._extensionProperties = input.newExtensionProperties;
        const newCartObject: Cart = {
            Id: this.cart.Id,
            ExtensionProperties: input.newExtensionProperties
        };
        return this.updateCart({ newCartObject });
    }

    /**
     * Updates cart object.
     * @param input - The input.
     * @param input.newCartObject - The new cart object.
     * @param input.additionalProperties - Additional properties.
     * @returns Promise of cart action result.
     */
    @action
    public async updateCart(input: { newCartObject: Cart; additionalProperties?: object }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() => updateCart(this.cart, input.newCartObject, this.actionContext));
        });
    }

    /**
     * Updates cartLines objects in cart.
     * @param input - The input.
     * @param input.updatedCartLinesObject - updated cartline.
     * @returns ICartActionResult.
     */
    @action
    public async updateCartLines(input: { updatedCartLinesObject: CartLine[] }): Promise<ICartActionResult> {
        return this._doAsyncAction<ICartActionResult>(async () => {
            return this._doCartOperationWithRetry(() =>
                updateCartLinesInternal(this.cart, input.updatedCartLinesObject, this.actionContext)
            );
        });
    }

    /**
     * Initialize function.
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        const newCart = await getOrCreateActiveCart(this.actionContext);

        if (newCart) {
            this._cart = newCart;
            this._status = 'READY';
        } else {
            this._status = 'ERROR';
        }

        this.isInitialized = true;
    }

    /**
     * Validates inventory across cart lines.
     * @param callback - Callback function.
     * @returns ICartActionResult.
     */
    private async _doCartOperationWithRetry(callback: () => Promise<ICartActionResultWithCart>): Promise<ICartActionResult> {
        let callbackResult = await callback();

        if (callbackResult.status === 'SUCCESS') {
            if (callbackResult.cart) {
                this._cart = callbackResult.cart;
            }
        } else {
            const refreshCartResult = await this.refreshCart({});

            if (refreshCartResult.status === 'SUCCESS') {
                callbackResult = await callback();

                if (callbackResult.status === 'SUCCESS') {
                    if (callbackResult.cart) {
                        this._cart = callbackResult.cart;
                    }
                }
            }
        }

        if (callbackResult.status === 'SUCCESS' && callbackResult.substatus && callbackResult.substatus === 'PRODUCTADDED') {
            this._isProductAddedToCart = true;
        } else {
            this._isProductAddedToCart = false;
        }

        return {
            status: callbackResult.status,
            substatus: callbackResult.substatus,
            errorDetails: callbackResult.errorDetails,
            validationResults: callbackResult.validationResults
        };
    }

    /**
     * Build address from org unit location.
     * @param location - The location.
     * @returns Address.
     */
    private _buildAddressFromOrgUnitLocation(location: OrgUnitLocation): Address {
        return {
            RecordId: location.PostalAddressId,
            Name: location.OrgUnitName,
            FullAddress: location.Address,
            Street: location.Street,
            StreetNumber: location.StreetNumber,
            City: location.City,
            DistrictName: location.DistrictName,
            BuildingCompliment: location.BuildingCompliment,
            Postbox: location.Postbox,
            ThreeLetterISORegionName: location.Country,
            ZipCode: location.Zip,
            County: location.County,
            CountyName: location.CountyName,
            State: location.State,
            StateName: location.StateName
        };
    }
}
